<template>
  <VaSelect
    v-model:is-open="isOpen"
    :model-value="modelValue"
    :options="optionsArray"
    :loading="loading"
    :placeholder="placeholder"
    :disabled="disabled"
    :text-by="textKey"
    :value-by="valueKey"
    :error="error"
    :id="inputId"
    v-bind="inputAriaProps"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabelledBy ?? undefined"
    :aria-describedby="ariaDescribedby"
    content-class="z-select-popover! [&_.va-select-option]:min-w-0 [&_.va-select-option]:overflow-hidden [&_.va-select-option]:text-ellipsis [&_.va-select-option]:whitespace-nowrap"
    @update:model-value="onUpdate"
  >
    <template #appendInner>
      <AppIcon
        class="va-select__toggle-icon"
        :name="isOpen ? IconName.ChevronUp : IconName.ChevronDown"
      />
    </template>
  </VaSelect>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue'
import { VaSelect } from 'vuestic-ui'
import AppIcon, { IconName } from './AppIcon.vue'

/**
 * Vuestic's `options` prop type is the private `SelectableOption` union
 * (`string | number | boolean | Record<string, any> | null | undefined`).
 * Our `AppSelectOption` is a structural subtype, but Vuestic does not
 * re-export the union publicly, so we declare a local shape that covers
 * every value Vuestic might pass back into our `textBy` / `valueBy`
 * projectors.
 */
type VuesticOptionProjection =
  | string
  | number
  | boolean
  | null
  | undefined
  | { readonly [key: string]: unknown }

export interface AppSelectOption {
  readonly text: string
  readonly value: string | number
}

export interface AppSelectProps {
  readonly modelValue: string | number | null
  readonly options: ReadonlyArray<AppSelectOption>
  readonly loading?: boolean
  readonly placeholder?: string
  readonly disabled?: boolean
  readonly error?: boolean
  readonly id?: string
  readonly ariaLabel?: string
  readonly ariaLabelledby?: string
  readonly ariaDescribedby?: string
}

interface VaSelectInputAriaProps {
  readonly inputAriaLabel?: string
  readonly inputAriaLabelledby?: string
  readonly inputAriaDescribedby?: string
}

const props = withDefaults(defineProps<AppSelectProps>(), {
  loading: false,
  placeholder: '',
  disabled: false,
  error: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void
}>()

const isOpen = ref<boolean>(false)

/**
 * Auto-generates an `id` via `useId()` and conditionally omits `aria-labelledby` when no label target is provided, preventing invalid references; pass them explicitly when you need stable cross-component handles.
 */
const generatedId = useId()
const inputId = computed(() => props.id ?? generatedId)
const ariaLabelledBy = computed(() => {
  const value = props.ariaLabelledby
  return value && value.length > 0 ? value : null
})

/**
 * Vuestic's `options` prop is typed as a mutable array; copy the readonly
 * array into a fresh mutable one so the bound-prop check passes. Each
 * element is widened to `VuesticOptionProjection` for the projection
 * callbacks below.
 */
const optionsArray = computed<VuesticOptionProjection[]>(() =>
  props.options.map((option) => ({ ...option })),
)
const inputAriaProps = computed<VaSelectInputAriaProps>(() => ({
  ...(props.ariaLabel === undefined
    ? {}
    : { inputAriaLabel: props.ariaLabel }),
  ...(props.ariaLabelledby === undefined
    ? {}
    : { inputAriaLabelledby: props.ariaLabelledby }),
  ...(props.ariaDescribedby === undefined
    ? {}
    : { inputAriaDescribedby: props.ariaDescribedby }),
}))

function textKey(option: VuesticOptionProjection): string {
  if (option !== null && typeof option === 'object') {
    const text = option['text']
    return String(text ?? '')
  }
  return String(option)
}

function valueKey(option: VuesticOptionProjection): string | number {
  if (option !== null && typeof option === 'object') {
    const v = option['value']
    if (typeof v === 'string' || typeof v === 'number') return v
    return String(v ?? '')
  }
  if (typeof option === 'string' || typeof option === 'number') return option
  return String(option ?? '')
}

function onUpdate(value: string | number | null): void {
  emit('update:modelValue', value)
}

function onWindowKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || !isOpen.value) return
  isOpen.value = false
  event.preventDefault()
}

watch(isOpen, (open) => {
  if (open) {
    window.addEventListener('keydown', onWindowKeydown, true)
    return
  }
  window.removeEventListener('keydown', onWindowKeydown, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeydown, true)
})
</script>
